import { useLocation, useNavigate, useParams } from 'react-router-dom';
import InfiniteScroll from '@/components/InfiniteScroll';
import useInfiniteRecords from '@/hooks/useInfiniteRecords';
import React, { useState, useEffect } from 'react';
import {
    Plus, Search, Edit2, Trash2, Save, X, RefreshCw,
    Building2, MapPin, Layers, Mail, UserCheck, ShieldAlert, CheckCircle2
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import api from '@/api/axios';
import { useAuth } from '@/auth/AuthContext';
import { EMAIL_INPUT_PATTERN, isValidEmail } from '@/utils/formValidation';

// Interfaces
export interface Secretary {
    id: string;
    name: string;
}

export interface Unit {
    id: string;
    name: string;
}

export interface Local {
    id: string;
    name: string;
}

export interface Applicant {
    id: string;
    name: string;
    email?: string;
    role?: string;
    localId: string;
    localName?: string;
    unitId?: string;
    unitName?: string;
    secretaryId?: string;
    secretaryName?: string;
}

export const ApplicantPage: React.FC = () => {
    const { activeSecretary } = useAuth();

    // ---------------------------------------------------------------------------
    // ESTADOS DE LISTAGEM E PAGINAÇÃO
    // ---------------------------------------------------------------------------
    const [searchTerm, setSearchTerm] = useState('');

    // ---------------------------------------------------------------------------
    // ESTADOS DO MODO DE EDIÇÃO / CRIAÇÃO
    // ---------------------------------------------------------------------------
    const navigate=useNavigate();const route=useLocation();const {id:routeId}=useParams<{id:string}>();
    const [isFormOpen, setIsFormOpen] = useState(false);
    const [editingId, setEditingId] = useState<string | null>(null);
    const [saving, setSaving] = useState(false);
    const [deletingId, setDeletingId] = useState<string | null>(null);

    // Campos do Formulário
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');

    // Cascata de Seleção do Local do Solicitante
    const [secretaries, setSecretaries] = useState<Secretary[]>([]);
    const [selectedSecretaryId, setSelectedSecretaryId] = useState('');
    const [units, setUnits] = useState<Unit[]>([]);
    const [selectedUnitId, setSelectedUnitId] = useState('');
    const [locations, setLocations] = useState<Local[]>([]);
    const [selectedLocalId, setSelectedLocalId] = useState('');

    const [loadingUnits, setLoadingUnits] = useState(false);
    const [loadingLocations, setLoadingLocations] = useState(false);

    // Feedback
    const [feedback, setFeedback] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

    const showFeedback = (type: 'success' | 'error', message: string) => {
        setFeedback({ type, message });
        setTimeout(() => setFeedback(null), 4000);
    };

    // ---------------------------------------------------------------------------
    // REQUISIÇÕES HTTP - BUSCA E PAGINAÇÃO
    // ---------------------------------------------------------------------------
    const query = useInfiniteRecords<Applicant>('/applicant/paged', { search: searchTerm.trim() || undefined }, activeSecretary?.id);
    const { items: applicants, loading, total: totalElements } = query;
    const fetchApplicants = async (_page?: number, _search?: string) => query.reload();
    const handleSearchSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        fetchApplicants(0, searchTerm);
    };

    // ---------------------------------------------------------------------------
    // CASCATA DE LOCALIZAÇÃO DO FORMULÁRIO
    // ---------------------------------------------------------------------------
    const loadSecretaries = async () => {
        setSecretaries(activeSecretary ? [activeSecretary] : []);
    };

    const loadUnits = async (secretaryId: string) => {
        if (!secretaryId) {
            setUnits([]);
            return;
        }
        setLoadingUnits(true);
        try {
            const res = await api.get(`/unit/secretary/${secretaryId}`);
            const data = res.data.content ?? res.data;
            setUnits(Array.isArray(data) ? data : []);
        } catch (err) {
            console.error("Erro ao carregar unidades:", err);
            setUnits([]);
        } finally {
            setLoadingUnits(false);
        }
    };

    const loadLocations = async (unitId: string) => {
        if (!unitId) {
            setLocations([]);
            return;
        }
        setLoadingLocations(true);
        try {
            const res = await api.get(`/local/unit/${unitId}`);
            const data = res.data.content ?? res.data;
            setLocations(Array.isArray(data) ? data : []);
        } catch (err) {
            console.error("Erro ao carregar locais:", err);
            setLocations([]);
        } finally {
            setLoadingLocations(false);
        }
    };

    // Efeitos da Cascata
    useEffect(() => {
        if (selectedSecretaryId) {
            loadUnits(selectedSecretaryId);
        } else {
            setUnits([]);
        }
    }, [selectedSecretaryId]);

    useEffect(() => {
        if (selectedUnitId) {
            loadLocations(selectedUnitId);
        } else {
            setLocations([]);
        }
    }, [selectedUnitId]);

    // ---------------------------------------------------------------------------
    // AÇÕES DO FORMULÁRIO (CRIAR, EDITAR, SALVAR, EXCLUIR)
    // ---------------------------------------------------------------------------
    const handleOpenCreate = () => {
        navigate('/applicants/new');
        setEditingId(null);
        setName('');
        setEmail('');
        setSelectedSecretaryId(activeSecretary?.id || '');
        setSelectedUnitId('');
        setSelectedLocalId('');
        loadSecretaries();
        setIsFormOpen(true);
    };

    const handleOpenEdit = async (applicant: Applicant) => {
        navigate(`/applicants/${applicant.id}/edit`);
        setEditingId(applicant.id);
        setName(applicant.name);
        setEmail(applicant.email || '');

        await loadSecretaries();

        if (applicant.secretaryId) {
            setSelectedSecretaryId(applicant.secretaryId);
            await loadUnits(applicant.secretaryId);
        }
        if (applicant.unitId) {
            setSelectedUnitId(applicant.unitId);
            await loadLocations(applicant.unitId);
        }
        if (applicant.localId) {
            setSelectedLocalId(applicant.localId);
        }

        setIsFormOpen(true);
    };

    const handleCloseForm = () => {
        navigate('/applicants');
        setIsFormOpen(false);
        setEditingId(null);
    };

    useEffect(()=>{
        if(route.pathname.endsWith('/new')) {handleOpenCreate();return;}
        if(!routeId){setIsFormOpen(false);return;}
        let alive=true;api.get(`/applicants/${routeId}`).then(r=>{if(alive)void handleOpenEdit(r.data);}).catch(()=>showFeedback('error','Não foi possível carregar o solicitante.'));
        return()=>{alive=false;};
    },[route.pathname,routeId]);

    const handleSave = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!name.trim()) {
            showFeedback('error', 'O nome do solicitante é obrigatório.');
            return;
        }
        if (email.trim() && !isValidEmail(email)) {
            showFeedback('error', 'Informe um e-mail válido, como usuario@dominio.com.br.');
            return;
        }
        if (!selectedLocalId) {
            showFeedback('error', 'Selecione o Local/Setor de lotação.');
            return;
        }

        setSaving(true);
        const payload = {
            name: name.trim(),
            email: email.trim() || null,
            localId: selectedLocalId
        };

        try {
            if (editingId) {
                await api.put(`/applicants/${editingId}`, payload);
                showFeedback('success', 'Solicitante atualizado com sucesso!');
            } else {
                await api.post('/applicants', payload);
                showFeedback('success', 'Solicitante cadastrado com sucesso!');
            }

            handleCloseForm();
            fetchApplicants(0, searchTerm);
        } catch (err: any) {
            console.error("Erro ao salvar:", err);
            showFeedback('error', err.response?.data?.message || 'Erro ao salvar informações do solicitante.');
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (id: string) => {
        if (!window.confirm('Tem certeza que deseja remover este solicitante?')) return;

        setDeletingId(id);
        try {
            await api.delete(`/applicants/${id}`);
            showFeedback('success', 'Solicitante removido com sucesso!');
            fetchApplicants(0, searchTerm);
        } catch (err: any) {
            console.error("Erro ao remover:", err);
            showFeedback('error', err.response?.data?.message || 'Erro ao excluir o solicitante.');
        } finally {
            setDeletingId(null);
        }
    };

    const containerVariants = {
        hidden: { opacity: 0, y: 15 },
        visible: { opacity: 1, y: 0, transition: { duration: 0.3 } }
    };

    return (
        <div className="page-content">
            <div className="space-y-6">

                <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="flex min-h-12 flex-col items-stretch justify-end gap-3 sm:flex-row sm:items-center"
                >
                        <AnimatePresence>
                            {feedback && (
                                <motion.div
                                    initial={{ opacity: 0, scale: 0.8 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                    exit={{ opacity: 0, scale: 0.8 }}
                                    className={`px-4 py-2.5 rounded-2xl text-xs font-bold flex items-center gap-2 shadow-lg ${
                                        feedback.type === 'success'
                                            ? 'bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 shadow-emerald-500/10'
                                            : 'bg-rose-500/10 border border-rose-500/30 text-rose-400 shadow-rose-500/10'
                                    }`}
                                >
                                    {feedback.type === 'success' ? <CheckCircle2 size={18} /> : <ShieldAlert size={18} />}
                                    {feedback.message}
                                </motion.div>
                            )}
                        </AnimatePresence>

                        {!isFormOpen && (
                            <motion.button
                                whileHover={{ scale: 1.02 }}
                                whileTap={{ scale: 0.98 }}
                                onClick={handleOpenCreate}
                                className="px-5 py-3 bg-blue-600 hover:bg-blue-500 text-white font-black text-xs uppercase tracking-wider rounded-2xl transition-all shadow-xl shadow-blue-600/20 flex items-center gap-2"
                            >
                                <Plus size={18} /> Novo Solicitante
                            </motion.button>
                        )}
                </motion.div>

                {/* PAINEL DE FORMULÁRIO (CADASTRAR / EDITAR) */}
                <AnimatePresence>
                    {isFormOpen && (
                        <motion.div
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: 'auto' }}
                            exit={{ opacity: 0, height: 0 }}
                            className="ui-card border-blue-500/30"
                        >
                            <div className="absolute top-0 right-0 w-40 h-40 bg-blue-500/10 rounded-full blur-3xl pointer-events-none" />

                            <div className="flex justify-between items-center border-b border-white/5 pb-4 mb-6">
                                <h2 className="text-xs font-black text-white uppercase tracking-wider flex items-center gap-2">
                                    <div className="p-1.5 bg-blue-500/10 text-blue-400 rounded-lg">
                                        {editingId ? <Edit2 size={16} /> : <Plus size={16} />}
                                    </div>
                                    {editingId ? 'Editar Solicitante' : 'Cadastrar Novo Solicitante'}
                                </h2>
                                <button
                                    onClick={handleCloseForm}
                                    className="p-2 text-slate-400 hover:text-white bg-white/5 hover:bg-white/10 rounded-xl transition-all"
                                >
                                    <X size={18} />
                                </button>
                            </div>

                            <form onSubmit={handleSave} className="space-y-6">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

                                    {/* NOME */}
                                    <div className="space-y-1">
                                        <label className="text-[10px] font-black text-slate-400 uppercase flex items-center gap-1 ml-1">
                                            <UserCheck size={12} className="text-blue-400"/> Nome Completo *
                                        </label>
                                        <input
                                            type="text"
                                            required
                                            placeholder="Ex: João da Silva"
                                            className="w-full bg-[#0b0f1a] border border-white/5 rounded-2xl p-3.5 text-xs text-white outline-none focus:border-blue-500/50 uppercase font-bold transition-all"
                                            value={name}
                                            onChange={e => setName(e.target.value)}
                                        />
                                    </div>

                                    {/* EMAIL */}
                                    <div className="space-y-1">
                                        <label className="text-[10px] font-black text-slate-400 uppercase flex items-center gap-1 ml-1">
                                            <Mail size={12}/> E-mail (Opcional)
                                        </label>
                                        <input
                                            type="email"
                                            pattern={EMAIL_INPUT_PATTERN}
                                            title="Informe um e-mail válido, como usuario@dominio.com.br."
                                            placeholder="exemplo@pref.sp.gov.br"
                                            className="w-full bg-[#0b0f1a] border border-white/5 rounded-2xl p-3.5 text-xs text-white outline-none focus:border-blue-500/50 font-bold transition-all"
                                            value={email}
                                            onChange={e => setEmail(e.target.value)}
                                        />
                                    </div>

                                </div>

                                {/* BLOCO DE LOCALIZAÇÃO (HIERARQUIA) */}
                                <div className="bg-[#0b0f1a]/50 border border-white/5 rounded-2xl p-4 space-y-4">
                                    <div className="text-[10px] font-black text-blue-400 uppercase tracking-wider flex items-center gap-1">
                                        <MapPin size={12}/> Lotação / Setor do Servidor
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">

                                        {/* SECRETARIA */}
                                        <div className="space-y-1">
                                            <label className="text-[10px] font-black text-slate-400 uppercase flex items-center gap-1 ml-1">
                                                <Building2 size={12}/> Secretaria
                                            </label>
                                            <select
                                                disabled
                                                className="w-full bg-[#0b0f1a] border border-white/5 rounded-2xl p-3 text-xs text-white outline-none focus:border-blue-500/50 uppercase font-bold transition-all"
                                                value={selectedSecretaryId}
                                                onChange={e => {
                                                    setSelectedSecretaryId(e.target.value);
                                                    setSelectedUnitId('');
                                                    setSelectedLocalId('');
                                                }}
                                            >
                                                <option value="">Selecione a Secretaria</option>
                                                {secretaries.map(sec => (
                                                    <option key={sec.id} value={sec.id}>{sec.name.toUpperCase()}</option>
                                                ))}
                                            </select>
                                        </div>

                                        {/* UNIDADE */}
                                        <div className="space-y-1">
                                            <label className="text-[10px] font-black text-slate-400 uppercase flex items-center gap-1 ml-1">
                                                <MapPin size={12}/> Unidade / Prédio
                                            </label>
                                            <select
                                                disabled={!selectedSecretaryId || loadingUnits}
                                                className="w-full bg-[#0b0f1a] border border-white/5 rounded-2xl p-3 text-xs text-white outline-none focus:border-blue-500/50 uppercase font-bold disabled:opacity-30 transition-all"
                                                value={selectedUnitId}
                                                onChange={e => {
                                                    setSelectedUnitId(e.target.value);
                                                    setSelectedLocalId('');
                                                }}
                                            >
                                                <option value="">
                                                    {loadingUnits ? 'Carregando...' : !selectedSecretaryId ? 'Selecione a Secretaria...' : 'Selecione a Unidade'}
                                                </option>
                                                {units.map(u => (
                                                    <option key={u.id} value={u.id}>{u.name.toUpperCase()}</option>
                                                ))}
                                            </select>
                                        </div>

                                        {/* LOCAL / SETOR */}
                                        <div className="space-y-1">
                                            <label className="text-[10px] font-black text-slate-400 uppercase flex items-center gap-1 ml-1">
                                                <Layers size={12}/> Local / Setor *
                                            </label>
                                            <select
                                                disabled={!selectedUnitId || loadingLocations}
                                                className="w-full bg-[#0b0f1a] border border-white/5 rounded-2xl p-3 text-xs text-white outline-none focus:border-blue-500/50 uppercase font-bold disabled:opacity-30 transition-all"
                                                value={selectedLocalId}
                                                onChange={e => setSelectedLocalId(e.target.value)}
                                            >
                                                <option value="">
                                                    {loadingLocations ? 'Carregando...' : !selectedUnitId ? 'Selecione a Unidade...' : 'Selecione o Setor'}
                                                </option>
                                                {locations.map(loc => (
                                                    <option key={loc.id} value={loc.id}>{loc.name.toUpperCase()}</option>
                                                ))}
                                            </select>
                                        </div>

                                    </div>
                                </div>

                                {/* BOTÕES DO FORMULÁRIO */}
                                <div className="flex justify-end gap-3 pt-2">
                                    <button
                                        type="button"
                                        onClick={handleCloseForm}
                                        className="px-5 py-3 bg-white/5 hover:bg-white/10 text-slate-300 font-black text-xs uppercase tracking-wider rounded-2xl transition-all"
                                    >
                                        Cancelar
                                    </button>
                                    <button
                                        type="submit"
                                        disabled={saving || !name.trim() || !selectedLocalId}
                                        className="px-6 py-3 bg-blue-600 hover:bg-blue-500 disabled:opacity-30 text-white font-black text-xs uppercase tracking-wider rounded-2xl transition-all shadow-xl shadow-blue-600/20 flex items-center gap-2"
                                    >
                                        {saving ? <RefreshCw className="animate-spin" size={16} /> : <Save size={16} />}
                                        {editingId ? 'Atualizar' : 'Salvar Registros'}
                                    </button>
                                </div>
                            </form>
                        </motion.div>
                    )}
                </AnimatePresence>

                {/* PAINEL DE BUSCA E LISTAGEM */}
                {!isFormOpen && <motion.div
                    variants={containerVariants}
                    initial="hidden"
                    animate="visible"
                    className="ui-card ui-card-hover space-y-6"
                >
                    {/* BARRA DE PESQUISA */}
                    <form onSubmit={handleSearchSubmit} className="flex flex-col md:flex-row gap-4 items-center justify-between">
                        <div className="relative w-full md:w-96">
                            <input
                                type="text"
                                placeholder="Buscar por nome do solicitante..."
                                value={searchTerm}
                                onChange={e => setSearchTerm(e.target.value)}
                                className="w-full bg-[#0b0f1a] border border-white/5 rounded-2xl pl-11 pr-4 py-3 text-xs text-white outline-none focus:border-blue-500/50 uppercase placeholder:text-slate-600 font-bold transition-all"
                            />
                            <Search className="absolute left-4 top-3.5 text-slate-500" size={16} />
                        </div>

                        <div className="flex items-center gap-3 w-full md:w-auto justify-between md:justify-end">
                            <span className="text-[10px] text-slate-500 font-mono">
                                Total: <strong className="text-white">{totalElements}</strong> registros
                            </span>
                            <button
                                type="submit"
                                className="px-4 py-3 bg-white/5 hover:bg-white/10 text-slate-300 font-black text-xs uppercase tracking-wider rounded-2xl transition-all flex items-center gap-2"
                            >
                                <RefreshCw size={14} className={loading ? 'animate-spin' : ''} /> Filtrar
                            </button>
                        </div>
                    </form>

                    {/* TABELA DE SOLICITANTES */}
                    <div className="overflow-x-auto">
                        <table className="w-full text-left border-collapse">
                            <thead>
                            <tr className="border-b border-white/5 text-[10px] font-black text-slate-400 uppercase tracking-widest">
                                <th className="p-4">Solicitante</th>
                                <th className="p-4">Setor / Local</th>
                                <th className="p-4">Unidade</th>
                                <th className="p-4">Secretaria</th>
                                <th className="p-4 text-right">Ações</th>
                            </tr>
                            </thead>
                            <tbody className="divide-y divide-white/5 text-xs">
                            {loading && !applicants.length ? (
                                <tr>
                                    <td colSpan={5} className="p-8 text-center text-slate-500">
                                        <RefreshCw className="animate-spin inline-block mr-2" size={18} /> Carregando registros...
                                    </td>
                                </tr>
                            ) : applicants.length === 0 ? (
                                <tr>
                                    <td colSpan={5} className="p-8 text-center text-slate-500 font-bold uppercase tracking-wider">
                                        Nenhum solicitante encontrado.
                                    </td>
                                </tr>
                            ) : (
                                applicants.map(app => (
                                    <tr key={app.id} className="hover:bg-white/[0.02] transition-colors group">
                                        <td className="p-4">
                                            <div className="font-bold text-white uppercase">{app.name}</div>
                                            {app.email && <div className="text-[10px] text-slate-500 font-mono">{app.email}</div>}
                                        </td>
                                        <td className="p-4 font-bold text-blue-400 uppercase">
                                            {app.localName || 'N/A'}
                                        </td>
                                        <td className="p-4 font-bold text-slate-300 uppercase">
                                            {app.unitName || 'N/A'}
                                        </td>
                                        <td className="p-4 text-slate-400 uppercase text-[11px]">
                                            {app.secretaryName || 'N/A'}
                                        </td>
                                        <td className="p-4 text-right space-x-2">
                                            <button
                                                onClick={() => handleOpenEdit(app)}
                                                className="p-2 text-slate-400 hover:text-blue-400 hover:bg-blue-500/10 rounded-xl transition-all"
                                                title="Editar Solicitante"
                                            >
                                                <Edit2 size={16} />
                                            </button>
                                            <button
                                                onClick={() => handleDelete(app.id)}
                                                disabled={deletingId === app.id}
                                                className="p-2 text-slate-400 hover:text-rose-400 hover:bg-rose-500/10 rounded-xl transition-all disabled:opacity-30"
                                                title="Excluir Solicitante"
                                            >
                                                {deletingId === app.id ? <RefreshCw className="animate-spin" size={16} /> : <Trash2 size={16} />}
                                            </button>
                                        </td>
                                    </tr>
                                ))
                            )}
                            </tbody>
                        </table>
                    </div>

                    {/* PAGINAÇÃO */}
                    {query.error && <p role="alert" className="feedback-error">{query.error}</p>}<InfiniteScroll hasMore={query.hasMore} loading={loading} onLoadMore={query.loadMore} />
                </motion.div>}

            </div>
        </div>
    );
};

export default ApplicantPage;
